import { LazyMotion, AnimatePresence } from "framer-motion";
import dynamic from "next/dynamic";
import { useEffect, useRef } from "react";
import { useState } from "react";
import StickyBox from "react-sticky-box";
import { shallow } from "zustand/shallow";

import BookingPageTagManager from "@calcom/app-store/BookingPageTagManager";
import dayjs from "@calcom/dayjs";
import { useEmbedType, useEmbedUiConfig, useIsEmbed } from "@calcom/embed-core/embed-iframe";
import { useNonEmptyScheduleDays } from "@calcom/features/schedules";
import classNames from "@calcom/lib/classNames";
import { useCurrencyStore } from "@calcom/lib/currencyStore";
import useMediaQuery from "@calcom/lib/hooks/useMediaQuery";
import { BookerLayouts, defaultBookerLayoutSettings } from "@calcom/prisma/zod-utils";

import { AvailableTimeSlots } from "./components/AvailableTimeSlots";
import { BookEventForm } from "./components/BookEventForm";
import { BookFormAsModal } from "./components/BookEventForm/BookFormAsModal";
import { EventMeta } from "./components/EventMeta";
import { Header } from "./components/Header";
import { LargeCalendar } from "./components/LargeCalendar";
import { BookerSection } from "./components/Section";
import { Away, NotFound } from "./components/Unavailable";
import { extraDaysConfig, fadeInLeft, getBookerSizeClassNames, useBookerResizeAnimation } from "./config";
import { useSyncBookingWithUrl } from "./hooks/useSyncBookingWithUrl";
import { useBookerStore, useInitializeBookerStore } from "./store";
import type { BookerLayout, BookerProps } from "./types";
import { useEvent, useScheduleForEvent } from "./utils/event";
import { validateLayout } from "./utils/layout";
import { getQueryParam } from "./utils/query-param";
import { useBrandColors } from "./utils/use-brand-colors";

const loadFramerFeatures = () => import("./framer-features").then((res) => res.default);
const PoweredBy = dynamic(() => import("@calcom/ee/components/PoweredBy"));
const UnpublishedEntity = dynamic(() => import("@calcom/ui").then((mod) => mod.UnpublishedEntity));
const DatePicker = dynamic(() => import("./components/DatePicker").then((mod) => mod.DatePicker), {
  ssr: false,
});

const OverrideBookerSection = () => {
  const setSelectedTimeslot = useBookerStore((state) => state.setSelectedTimeslot);
  const [seatedEventData, setSeatedEventData] = useBookerStore(
    (state) => [state.seatedEventData, state.setSeatedEventData],
    shallow
  );

  // TODO add a meta area here

  return (
    <BookerSection
      area="main"
      className="border-subtle sticky top-0 ml-[-1px] h-full p-6 md:w-[var(--booker-main-width)] md:border-l">
      <BookEventForm
      // onCancel={() => {
      //   setSelectedTimeslot(null);
      //   if (seatedEventData.bookingUid) {
      //     setSeatedEventData({ ...seatedEventData, bookingUid: undefined, attendees: undefined });
      //   }
      // }}
      />
    </BookerSection>
  );
};

const BookerComponent = ({
  username,
  eventSlug,
  month,
  bookingData,
  hideBranding = false,
  isTeamEvent,
  entity,
  duration,
}: BookerProps) => {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  const { currency, rates } = useCurrencyStore();
  const isEmbed = useIsEmbed();
  const embedUiConfig = useEmbedUiConfig();
  const event = useEvent();
  const bookerLayouts = event.data?.profile?.bookerLayouts || defaultBookerLayoutSettings;
  const rescheduleUid =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("rescheduleUid") : null;

  const bookingUid =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("bookingUid") : null;

  // I would expect isEmbed to be not needed here as it's handled in derived variable layout, but somehow removing it breaks the views.
  const defaultLayout = isEmbed
    ? validateLayout(embedUiConfig.layout) || bookerLayouts.defaultLayout
    : bookerLayouts.defaultLayout;

  // 1) Initialise the store (this will force booking_override on zero-min/no-meet)
  useInitializeBookerStore({
    username,
    eventSlug,
    month,
    eventId: event?.data?.id,
    rescheduleUid,
    bookingUid,
    bookingData,
    layout: defaultLayout,
    isTeamEvent,
    org: entity.orgSlug,
    // Narrow-cast metadata just for this optional field
    durationConfig: (event.data?.metadata as { multipleDuration?: number[] } | undefined)?.multipleDuration,
    eventLength: event.data?.length,
  });

  // 2) sync URL → store, but bail out if we're already in booking_override
  useSyncBookingWithUrl(username, eventSlug, event.data?.length ?? null);
  /**
   * Prioritize dateSchedule load
   * Component will render but use data already fetched from here, and no duplicate requests will be made
   * */
  const schedule = useScheduleForEvent({
    prefetchNextMonth: false,
    username,
    eventSlug,
    month,
    duration,
  });
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");
  const timeslotsRef = useRef<HTMLDivElement>(null);
  const StickyOnDesktop = isMobile ? "div" : StickyBox;
  const [_layout, setLayout] = useBookerStore((state) => [state.layout, state.setLayout], shallow);

  const embedType = useEmbedType();
  // Floating Button and Element Click both are modal and thus have dark background
  const hasDarkBackground = isEmbed && embedType !== "inline";

  // In Embed we give preference to embed configuration for the layout.If that's not set, we use the App configuration for the event layout
  // But if it's mobile view, there is only one layout supported which is 'mobile'
  const layout = isEmbed ? (isMobile ? "mobile" : validateLayout(embedUiConfig.layout) || _layout) : _layout;
  const columnViewExtraDays = useRef<number>(
    isTablet ? extraDaysConfig[layout].tablet : extraDaysConfig[layout].desktop
  );

  const [bookerState, setBookerState] = useBookerStore((state) => [state.state, state.setState], shallow);
  const selectedDate = useBookerStore((state) => state.selectedDate);
  const [selectedTimeslot, setSelectedTimeslot] = useBookerStore(
    (state) => [state.selectedTimeslot, state.setSelectedTimeslot],
    shallow
  );
  // const seatedEventData = useBookerStore((state) => state.seatedEventData);
  const [seatedEventData, setSeatedEventData] = useBookerStore(
    (state) => [state.seatedEventData, state.setSeatedEventData],
    shallow
  );
  // const [isBookingOverride, setIsBookingOverride] = useState(false);

  // minutes of the event (0 = price-only event, no timeslot)
  const eventLength = event.data?.length ?? 0;

  // Handle clicking outside the modal
  const handleModalCancel = () => {
    if (eventLength > 0) {
      // normal event with time slots → cancel = reset and go back
      setSelectedTimeslot(null);
      if (seatedEventData.bookingUid) {
        setSeatedEventData({
          ...seatedEventData,
          bookingUid: undefined,
          attendees: undefined,
        });
      }
    }
    // else: price-only (0 min) → do nothing (no-op)
  };
  const [nftUri, setNftUri] = useState("");

  const date = dayjs(selectedDate).format("YYYY-MM-DD");

  const nonEmptyScheduleDays = useNonEmptyScheduleDays(schedule?.data?.slots).filter(
    (slot) => dayjs(selectedDate).diff(slot, "day") <= 0
  );

  const extraDays = isTablet ? extraDaysConfig[layout].tablet : extraDaysConfig[layout].desktop;
  const animationScope = useBookerResizeAnimation(layout, bookerState);
  const totalWeekDays = 7;
  const addonDays =
    nonEmptyScheduleDays.length < extraDays
      ? (extraDays - nonEmptyScheduleDays.length + 1) * totalWeekDays
      : nonEmptyScheduleDays.length === extraDays
      ? totalWeekDays
      : 0;

  // Taking one more available slot(extraDays + 1) to calculate the no of days in between, that next and prev button need to shift.
  const availableSlots = nonEmptyScheduleDays.slice(0, extraDays + 1);
  if (nonEmptyScheduleDays.length !== 0)
    columnViewExtraDays.current =
      Math.abs(dayjs(selectedDate).diff(availableSlots[availableSlots.length - 2], "day")) + addonDays;
  const prefetchNextMonth =
    dayjs(date).month() !== dayjs(date).add(columnViewExtraDays.current, "day").month();
  const monthCount =
    dayjs(date).add(1, "month").month() !== dayjs(date).add(columnViewExtraDays.current, "day").month()
      ? 2
      : undefined;
  const nextSlots =
    Math.abs(dayjs(selectedDate).diff(availableSlots[availableSlots.length - 1], "day")) + addonDays;

  useBrandColors({
    brandColor: event.data?.profile.brandColor,
    darkBrandColor: event.data?.profile.darkBrandColor,
    theme: event.data?.profile.theme,
  });

  useEffect(() => {
    async function checkNFTDetails() {
      // bail: no event yet or already have it
      if (!event?.data || nftUri) return;

      const appMeta = event.data.metadata?.apps?.["wer-payment-two"] as
        | { nftid?: number | string; price?: number }
        | undefined;

      // bail: free artwork (no payment, no NFT lookup needed)
      if (!appMeta || appMeta.price === 0) return;

      // bail: no NFT id -> do NOT call the endpoint
      const nftId = appMeta.nftid ? Number(appMeta.nftid) : undefined;
      if (!nftId || Number.isNaN(nftId)) return;

      try {
        const res = await fetch("/api/integrations/wer-payment-two/nftdata", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: nftId }),
        });

        if (!res.ok) {
          console.error("NFT details fetch failed", res.status);
          return; // do not break payment flow
        }

        const data: { uri?: string } = await res.json();
        if (data?.uri) setNftUri(data.uri);
      } catch (err) {
        console.error("Error fetching NFT details:", err);
      }
    }

    checkNFTDetails();
  }, [event?.data, nftUri]);

  useEffect(() => {
    if (isMobile && layout !== "mobile") {
      setLayout("mobile");
    } else if (!isMobile && layout === "mobile") {
      setLayout(defaultLayout);
    }
    console.log("YOOOYOYOYOYOYOOCT", event.data);
  }, [isMobile, setLayout, layout, defaultLayout]);

  //setting layout from query param
  useEffect(() => {
    const layout = getQueryParam("layout") as BookerLayouts;
    if (
      !isMobile &&
      !isEmbed &&
      validateLayout(layout) &&
      bookerLayouts?.enabledLayouts?.length &&
      layout !== _layout
    ) {
      const validLayout = bookerLayouts.enabledLayouts.find((userLayout) => userLayout === layout);
      validLayout && setLayout(validLayout);
    }
  }, [bookerLayouts, validateLayout, setLayout, _layout]);

  // read the current store state so our effect can bail early
  const storeState = useBookerStore((s) => s.state);

  useEffect(() => {
    if (event.isLoading) {
      setBookerState("loading");
      return;
    }

    const len = event.data?.length ?? 0;

    // Always require a date first (even for zero-minute events)
    if (!selectedDate) {
      setBookerState("selecting_date");
      return;
    }

    if (len > 0) {
      // Normal events: date -> time -> booking
      if (!selectedTimeslot) {
        setBookerState("selecting_time");
      } else {
        setBookerState("booking");
      }
    } else {
      // Zero-minute events: date -> booking (skip time step)
      setBookerState("booking");
    }
  }, [event.isLoading, event.data?.length, selectedDate, selectedTimeslot, setBookerState]);

  useEffect(() => {
    if (layout === "mobile") {
      timeslotsRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [layout]);

  const hideEventTypeDetails = isEmbed ? embedUiConfig.hideEventTypeDetails : false;

  if (entity.isUnpublished) {
    return <UnpublishedEntity {...entity} />;
  }

  if (event.isSuccess && !event.data) {
    return <NotFound />;
  }

  // In Embed, a Dialog doesn't look good, we disable it intentionally for the layouts that support showing Form without Dialog(i.e. no-dialog Form)
  const shouldShowFormInDialogMap: Record<BookerLayout, boolean> = {
    // mobile supports showing the Form without Dialog
    mobile: !isEmbed,
    // We don't show Dialog in month_view currently. Can be easily toggled though as it supports no-dialog Form
    month_view: false,
    // week_view doesn't support no-dialog Form
    // When it's supported, disable it for embed
    week_view: true,
    // column_view doesn't support no-dialog Form
    // When it's supported, disable it for embed
    column_view: true,
  };

  const shouldShowFormInDialog = shouldShowFormInDialogMap[layout];

  // --- Price selection: prefer server-converted cents if they match the current currency,
  // otherwise fall back to client-side conversion from USD cents using store rates.
  const appMeta = event.data?.metadata?.apps?.["wer-payment-two"] as
    | { price?: number; priceConvertedCents?: number; currency?: string }
    | undefined;

  const usdCents = Number(appMeta?.price ?? 0);

  const serverConvertedCents =
    typeof appMeta?.priceConvertedCents === "number" && appMeta?.currency === currency
      ? appMeta.priceConvertedCents
      : undefined;

  // Fallback = convert USD cents -> target cents using store rates
  const rate = rates?.[currency] ?? 1;
  const fallbackCents = Math.round(usdCents * rate);

  const centsToShow = typeof serverConvertedCents === "number" ? serverConvertedCents : fallbackCents;

  if (bookerState === "loading") {
    return null;
  }
  if (!hasMounted) return null;
  return (
    <>
      {event.data ? <BookingPageTagManager eventType={event.data} /> : null}
      <div
        className={classNames(
          // In a popup embed, if someone clicks outside the main(having main class or main tag), it closes the embed
          "main",
          "text-default flex min-h-full w-full flex-col items-center",
          layout === BookerLayouts.MONTH_VIEW ? "overflow-visible" : "overflow-clip"
        )}>
        <div
          ref={animationScope}
          className={classNames(
            ...getBookerSizeClassNames(layout, bookerState, hideEventTypeDetails),
            "bg-default dark:bg-muted grid max-w-full items-start dark:[color-scheme:dark] sm:transition-[width] sm:duration-300 sm:motion-reduce:transition-none",
            // We remove border only when the content covers entire viewport. Because in embed, it can almost never be the case that it covers entire viewport, we show the border there
            (layout === BookerLayouts.MONTH_VIEW || isEmbed) && "border-subtle rounded-md border",
            !isEmbed && "sm:transition-[width] sm:duration-300",
            isEmbed && layout === BookerLayouts.MONTH_VIEW && "border-booker sm:border-booker-width",
            !isEmbed && layout === BookerLayouts.MONTH_VIEW && "border-subtle"
          )}>
          <AnimatePresence>
            {bookerState === "booking_override" ? (
              <>
                {/* LEFT: Meta column (uses the same safe component as normal flow) */}
                <BookerSection
                  area="meta"
                  className="max-w-screen flex w-full flex-col md:w-[var(--booker-meta-width)]">
                  {nftUri && (
                    <div
                      className="min-h-40 mt-4 h-full w-full rounded-sm bg-contain bg-center bg-no-repeat"
                      style={{ backgroundImage: `url(${nftUri})` }}
                    />
                  )}
                  <EventMeta />
                </BookerSection>

                {/* RIGHT: Form column */}
                <OverrideBookerSection />
              </>
            ) : (
              <>
                <BookerSection
                  area="header"
                  className={classNames(
                    layout === BookerLayouts.MONTH_VIEW && "fixed top-4 z-10 ltr:right-4 rtl:left-4",
                    (layout === BookerLayouts.COLUMN_VIEW || layout === BookerLayouts.WEEK_VIEW) &&
                      "bg-default dark:bg-muted sticky top-0 z-10"
                  )}>
                  <Header
                    enabledLayouts={bookerLayouts.enabledLayouts}
                    extraDays={layout === BookerLayouts.COLUMN_VIEW ? columnViewExtraDays.current : extraDays}
                    isMobile={isMobile}
                    nextSlots={nextSlots}
                  />
                </BookerSection>

                <StickyOnDesktop
                  key="meta"
                  className={classNames(
                    "relative z-10 flex [grid-area:meta]",
                    layout !== BookerLayouts.MONTH_VIEW && !isEmbed && "sm:min-h-screen"
                  )}>
                  <BookerSection
                    area="meta"
                    className="max-w-screen flex w-full flex-col md:w-[var(--booker-meta-width)]">
                    {nftUri && (
                      <div
                        className="min-h-40 mt-4 h-full w-full rounded-sm bg-contain bg-center bg-no-repeat"
                        style={{ backgroundImage: `url(${nftUri})` }}
                      />
                    )}
                    <EventMeta />
                    {layout !== BookerLayouts.MONTH_VIEW &&
                      !(layout === "mobile" && bookerState === "booking") && (
                        <div className="mt-auto px-5 py-3">
                          <DatePicker />
                        </div>
                      )}
                  </BookerSection>
                </StickyOnDesktop>

                <BookerSection
                  key="book-event-form"
                  area="main"
                  className="border-subtle sticky top-0 ml-[-1px] h-full p-6 md:w-[var(--booker-main-width)] md:border-l"
                  {...fadeInLeft}
                  visible={bookerState === "booking" && !shouldShowFormInDialog}>
                  <BookEventForm
                    onCancel={() => {
                      setSelectedTimeslot(null);
                      if (seatedEventData.bookingUid) {
                        setSeatedEventData({
                          ...seatedEventData,
                          bookingUid: undefined,
                          attendees: undefined,
                        });
                      }
                    }}
                  />
                </BookerSection>

                <BookerSection
                  key="datepicker"
                  area="main"
                  visible={bookerState !== "booking" && layout === BookerLayouts.MONTH_VIEW}
                  {...fadeInLeft}
                  initial="visible"
                  className="md:border-subtle ml-[-1px] h-full flex-shrink px-5 py-3 md:border-l lg:w-[var(--booker-main-width)]">
                  <DatePicker />
                </BookerSection>

                <BookerSection
                  key="large-calendar"
                  area="main"
                  visible={layout === BookerLayouts.WEEK_VIEW}
                  className="border-subtle sticky top-0 ml-[-1px] h-full md:border-l"
                  {...fadeInLeft}>
                  <LargeCalendar extraDays={extraDays} />
                </BookerSection>

                <BookerSection
                  key="timeslots"
                  area={{ default: "main", month_view: "timeslots" }}
                  visible={
                    (layout !== BookerLayouts.WEEK_VIEW && bookerState === "selecting_time") ||
                    layout === BookerLayouts.COLUMN_VIEW
                  }
                  className={classNames(
                    "border-subtle rtl:border-default flex h-full w-full flex-col overflow-x-auto px-5 py-3 pb-0 rtl:border-r ltr:md:border-l",
                    layout === BookerLayouts.MONTH_VIEW &&
                      "scroll-bar h-full overflow-auto md:w-[var(--booker-timeslots-width)]",
                    layout !== BookerLayouts.MONTH_VIEW && "sticky top-0"
                  )}
                  ref={timeslotsRef}
                  {...fadeInLeft}>
                  <AvailableTimeSlots
                    extraDays={extraDays}
                    limitHeight={layout === BookerLayouts.MONTH_VIEW}
                    prefetchNextMonth={prefetchNextMonth}
                    monthCount={monthCount}
                    seatsPerTimeSlot={event.data?.seatsPerTimeSlot}
                  />
                </BookerSection>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* <m.span
          key="logo"
          className={classNames(
            "-z-10 mb-6 mt-auto pt-6 [&_img]:h-[15px]",
            hasDarkBackground ? "dark" : "",
            layout === BookerLayouts.MONTH_VIEW ? "block" : "hidden"
          )}
        /> */}
      </div>

      <BookFormAsModal
        visible={bookerState === "booking" && shouldShowFormInDialog}
        onCancel={handleModalCancel}
      />
    </>
  );
};

export const Booker = (props: BookerProps) => {
  if (props.isAway) return <Away />;

  return (
    <LazyMotion strict features={loadFramerFeatures}>
      <BookerComponent {...props} />
    </LazyMotion>
  );
};
